import { type ChatMessage, newQueravelChatId } from "@rhema/client";
import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { useIsDesktop } from "../app/media";

/** A chat Queravel can read. */
export type QueravelSource = {
  chatId: string;
  /** What the panel calls the chat, such as "#general" or "Amara Okafor". */
  title: string;
  /** Asking about a thread: the line saying Queravel was asked goes in the thread. */
  threadRoot?: string;
  /** Messages the person picked. Without them Queravel reads the chat's recent messages. */
  messages?: ChatMessage[];
};

/** The chat on screen, which the panel reads unless the person picked something else. */
export type QueravelSurface = { chatId: string; title: string };

export type PanelView = "chat" | "history";

type OpenOptions = {
  /** Something the person asked about from a chat. Without it the panel reads the chat on screen. */
  about?: QueravelSource;
  /** A stored conversation to show. */
  conversation?: string;
  /** Starts a new conversation. */
  fresh?: boolean;
  view?: PanelView;
};

export type QueravelPanelValue = {
  /** Whether the panel is open. Phones show it on its own screen, at /queravel. */
  open: boolean;
  view: PanelView;
  /** The conversation on show, or null for the newest stored one. */
  conversation: string | null;
  /** A conversation id for when there is nothing stored yet. The same until a new one starts. */
  fallback: string;
  /** The chat the panel reads, unless the person chose "Nothing, just me". */
  about: QueravelSource | null;
  /** The person chose to ask about nothing in Rhema. */
  alone: boolean;
  /** Opens the panel. On phones it takes the full screen. */
  show: (options?: OpenOptions) => void;
  /** Opens the panel, or closes it when it is open. */
  toggle: () => void;
  /** Closes the panel and goes back to where the person was. */
  close: () => void;
  setView: (view: PanelView) => void;
  setAlone: (alone: boolean) => void;
  /** Starts a new conversation. */
  startNew: () => void;
  pick: (conversation: string) => void;
  /** Called by the chat on screen. Returns a function that takes it off. */
  register: (surface: QueravelSurface) => () => void;
  /** Remembers which chat a conversation's questions were about. */
  noteSource: (conversation: string, chatId: string | null) => void;
  /** Puts text in a chat's composer. `key` is a chat id, or a chat id and thread root. */
  handOff: (key: string, text: string) => void;
  /** The text waiting for a composer, if there is some. */
  waiting: Record<string, string>;
  take: (key: string) => void;
  /** Where desktop goes back to from /queravel: the last screen before it. */
  lastPlace: () => string;
};

const noop = () => undefined;

const fallbackValue: QueravelPanelValue = {
  open: false,
  view: "chat",
  conversation: null,
  fallback: "",
  about: null,
  alone: false,
  show: noop,
  toggle: noop,
  close: noop,
  setView: noop,
  setAlone: noop,
  startNew: noop,
  pick: noop,
  register: () => noop,
  noteSource: noop,
  handOff: noop,
  waiting: {},
  take: noop,
  lastPlace: () => "/chats",
};

const PanelContext = createContext<QueravelPanelValue>(fallbackValue);

/** The key a composer waits under for a reply Queravel drafted. */
export function replyKey(chatId: string, threadRoot?: string): string {
  return threadRoot ? `${chatId}/${threadRoot}` : chatId;
}

/**
 * The one Queravel panel. Every way into Queravel opens it: the rail, ⌘J, the chat header, the
 * composer and the message actions. On desktop it sits beside whatever is open. On phones it is a
 * screen of its own at /queravel.
 */
export function QueravelPanelProvider({ children }: { children: ReactNode }) {
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PanelView>("chat");
  const [conversation, setConversation] = useState<string | null>(null);
  const [fallback, setFallback] = useState(newQueravelChatId);
  const [pinned, setPinned] = useState<QueravelSource | null>(null);
  const [alone, setAlone] = useState(false);
  const [surface, setSurface] = useState<QueravelSurface | null>(null);
  const [waiting, setWaiting] = useState<Record<string, string>>({});
  const sources = useRef(new Map<string, string | null>());
  const lastPlace = useRef("/chats");
  const placeBefore = useCallback(() => lastPlace.current, []);
  const onQueravel = location.pathname.startsWith("/queravel");

  useEffect(() => {
    if (!onQueravel) lastPlace.current = `${location.pathname}${location.search}`;
  }, [onQueravel, location.pathname, location.search]);

  // On phones the panel is its own screen. Leaving it, by any route, closes it.
  useEffect(() => {
    if (desktop || onQueravel) return;
    setOpen(false);
    setPinned(null);
    setAlone(false);
  }, [desktop, onQueravel]);

  // Picked messages belong to their chat. Moving to another chat reads that chat instead.
  useEffect(() => {
    if (desktop && surface && pinned && pinned.chatId !== surface.chatId) setPinned(null);
  }, [desktop, surface, pinned]);

  const about = pinned && (!desktop || surface?.chatId === pinned.chatId) ? pinned : surface;

  const startNew = useCallback(() => {
    const id = newQueravelChatId();
    setFallback(id);
    setConversation(id);
    setView("chat");
  }, []);

  const show = useCallback(
    (options: OpenOptions = {}) => {
      const { about: next, conversation: stored, fresh, view: nextView } = options;
      if (next) {
        setPinned(next);
        setAlone(false);
        // A question about another chat starts its own conversation, so each one is about one chat.
        const current = conversation ?? fallback;
        if (sources.current.get(current) !== next.chatId) startNew();
      }
      if (stored) setConversation(stored);
      if (fresh) startNew();
      setView(nextView ?? (next || stored || fresh ? "chat" : view));
      setOpen(true);
      if (!desktop && !location.pathname.startsWith("/queravel")) navigate("/queravel");
    },
    [conversation, fallback, startNew, view, desktop, location.pathname, navigate],
  );

  const close = useCallback(() => {
    setOpen(false);
    setPinned(null);
    setAlone(false);
    if (desktop || !location.pathname.startsWith("/queravel")) return;
    if (location.key !== "default") navigate(-1);
    else navigate("/chats", { replace: true });
  }, [desktop, location.pathname, location.key, navigate]);

  const toggle = useCallback(() => {
    if (open && (desktop || onQueravel)) close();
    else show();
  }, [open, desktop, onQueravel, close, show]);

  // ⌘J or Ctrl+J opens and closes the panel from anywhere on desktop.
  useEffect(() => {
    if (!desktop) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [desktop, toggle]);

  const register = useCallback((next: QueravelSurface) => {
    setSurface(next);
    return () => setSurface((s) => (s === next ? null : s));
  }, []);

  const noteSource = useCallback((id: string, chatId: string | null) => {
    if (!sources.current.has(id)) sources.current.set(id, chatId);
  }, []);

  const handOff = useCallback((key: string, text: string) => {
    setWaiting((w) => ({ ...w, [key]: text }));
  }, []);

  const take = useCallback((key: string) => {
    setWaiting((w) => {
      if (!(key in w)) return w;
      const { [key]: _, ...rest } = w;
      return rest;
    });
  }, []);

  const pick = useCallback((id: string) => {
    setConversation(id);
    setView("chat");
  }, []);

  const value = useMemo<QueravelPanelValue>(
    () => ({
      open: desktop ? open : onQueravel,
      view,
      conversation,
      fallback,
      about,
      alone,
      show,
      toggle,
      close,
      setView,
      setAlone,
      startNew,
      pick,
      register,
      noteSource,
      handOff,
      waiting,
      take,
      lastPlace: placeBefore,
    }),
    [
      desktop,
      open,
      onQueravel,
      view,
      conversation,
      fallback,
      about,
      alone,
      show,
      toggle,
      close,
      startNew,
      pick,
      register,
      noteSource,
      handOff,
      waiting,
      take,
      placeBefore,
    ],
  );

  return createElement(PanelContext.Provider, { value }, children);
}

export function useQueravelPanel(): QueravelPanelValue {
  return useContext(PanelContext);
}

/** Tells the panel which chat is on screen, while it is. */
export function useQueravelSurface(surface: QueravelSurface | null) {
  const { register } = useQueravelPanel();
  const chatId = surface?.chatId;
  const title = surface?.title;
  useEffect(() => {
    if (!chatId || title === undefined) return;
    return register({ chatId, title });
  }, [register, chatId, title]);
}

/** Fills a composer with a reply drafted in the panel, once one is waiting for it. */
export function useDraftedReply(key: string | null, fill: (text: string) => void) {
  const { waiting, take } = useQueravelPanel();
  const text = key ? waiting[key] : undefined;
  const latest = useRef(fill);
  latest.current = fill;
  useEffect(() => {
    if (!key || text === undefined) return;
    latest.current(text);
    take(key);
  }, [key, text, take]);
}
